/**
 * useEffectiveUserId — Single source of truth for the "current" user ID.
 *
 * During normal operation this returns the authenticated user's ID.
 * When an admin is impersonating another user it returns the impersonated
 * user's ID instead, so every data query automatically targets the right
 * account.
 *
 * Usage:
 *   const effectiveUserId = useEffectiveUserId();
 *   // Use effectiveUserId everywhere you previously used user.id for
 *   // data queries, content persistence, etc.
 *
 * For operations that MUST always use the real admin ID (e.g. audit
 * logging, admin-only mutations), continue using `useAuth().user.id`.
 */

import { useAuth } from "@/contexts/AuthContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";

export function useEffectiveUserId(): string | undefined {
  const { user } = useAuth();
  const { isImpersonating, impersonatedUserId } = useImpersonation();

  return isImpersonating && impersonatedUserId
    ? impersonatedUserId
    : user?.id;
}
